import React from "react";
import { Row, Col, Tabs, Tab, OverlayTrigger, Tooltip } from "react-bootstrap";
import HospitalCensus from "./HospitalCensus";
import HospitalAdmission from "./HospitalAdmission";
import TooltipHelper from "../../../components/TooltipHelper";
import NoDataSkeleton from "../../../components/NoDataSkeleton";
import { refAreaCalc } from "../../../utils/DataConversion";

interface HospitalizationProps {
  currentFilter: App.CurrentFilter;
  data: {
    census: App.HospitalCensusRecord[];
    admissions: App.HospitalRecord[];
  };
  reportDate: string | null;
  dateFilter: boolean;
}

export default function Hospitalization(props: HospitalizationProps) {
  const { data, currentFilter, reportDate, dateFilter } = props;

  return (
    <Row>
      <Col xs={12} lg={12} className="h-100 mb-2">
        <div className="d-flex justify-content-between">
          <h3 className="mb-3">Hospitalizations</h3>
          {dateFilter ? (
            <TooltipHelper
              toolTipClass=" mt-3"
              altIcon="far fa-calendar"
              text="Filtering on Date Range"
            />
          ) : null}
        </div>
        <Tabs defaultActiveKey="census" id="resource">
          <Tab eventKey="census" title="Census">
            <div className="d-flex">
              <h5>{`${
                currentFilter.filterType === "regional"
                  ? currentFilter.displayName
                  : currentFilter.regionName
                  ? currentFilter.regionName
                  : "Statewide"
              } COVID-19 Hospital Census`}</h5>
              <TooltipHelper
                text="These numbers are provisional and only reflect data that has been reported to IDOH’s EM Resources database.  
                The daily hospitalization census data represents all confirmed and PUI COVID-19 patients currently in a hospital bed on that given day."
              />
            </div>
            {data.census.length ? (
              <HospitalCensus name="Census" data={data.census} />
            ) : (
              <NoDataSkeleton
                message={`No COVID-19 census data for the following section: ${currentFilter.displayName}.`}
              />
            )}
          </Tab>
          <Tab eventKey="rate" title="Admissions">
            <Row>
              <Col md={12} className="d-flex">
                <h5>{`${
                  currentFilter.filters.length
                    ? currentFilter.displayName
                    : "Statewide"
                } COVID-19 Hospital Admissions`}</h5>
                <TooltipHelper text="Hospital census data is provided by the Regenstrief Institute in collaboration with the Indiana Health Information Exchange.  Minor differences in the daily admission data on the state dashboard as compared to the Regenstrief dashboard may exist." />
              </Col>
              <Col md={12}>
                <p>
                  For more information on hospitalizations,{" "}
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.regenstrief.org/covid-dashboard/"
                  >
                    visit Regenstrief Institute's dashboard.
                  </a>
                </p>
              </Col>
              <Col xs={12} md={{ offset: 9, span: 3 }}>
                <div className="float-right">
                  <OverlayTrigger
                    key="bottom"
                    placement="bottom"
                    overlay={
                      <Tooltip id="tooltip-bottom">
                        Receiving hospitalization data typically takes 3-5 days.
                        Therefore, the data within the preliminary shaded area
                        are provisional and should not be characterized as
                        comprehensive and complete data.
                      </Tooltip>
                    }
                  >
                    <p
                      className="mt-1 text-right cursor-pointer"
                      style={{
                        textDecoration: "underline dotted",
                        fontSize: ".8rem",
                      }}
                    >
                      {" "}
                      What does prelim mean?{" "}
                    </p>
                  </OverlayTrigger>
                </div>
              </Col>
            </Row>
            {data.admissions.length ? (
              <HospitalAdmission
                name="Admissions"
                startPre={refAreaCalc(reportDate)}
                data={data.admissions}
              />
            ) : (
              <NoDataSkeleton
                message={`No COVID-19  admissions data for the following section: ${currentFilter.displayName}.`}
              />
            )}
          </Tab>
        </Tabs>
      </Col>
    </Row>
  );
}
